import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Check, X, ClipboardList, Loader2, FileSpreadsheet, Send, MessageSquare } from 'lucide-react';

export default function TimesheetApprovals() {
  const [tab, setTab] = useState('submitted');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejecting, setRejecting] = useState(null); // submission id being rejected
  const [rejectNote, setRejectNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await api.post('/time-tracking/admin/send-weekly-digest', {});
      if (res.data?.sent) {
        toast.success(`Digest emailed to ${res.data.delivered?.length || 0} admin(s) for week ending ${res.data.week_ending}.`);
      } else if (res.data?.reason === 'already_sent') {
        // Try a force re-send.
        const forced = await api.post('/time-tracking/admin/send-weekly-digest?force=true', {});
        toast.success(`Digest re-sent to ${forced.data?.delivered?.length || 0} admin(s).`);
      } else {
        toast.message(res.data?.reason || 'Digest not sent');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const sendFridayReminders = async () => {
    setSendingReminders(true);
    try {
      // Force=true so admins can use this button anytime, not just on a fresh week
      const res = await api.post('/time-tracking/admin/send-friday-reminders?force=true', {});
      const data = res.data || {};
      if (data.sent) {
        toast.success(`Sent SMS reminder to ${data.sent_count} employee(s); skipped ${data.skipped_count}.`);
      } else {
        toast.message(data.reason || 'No reminders sent');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await api.get(`/time-tracking/submissions?status_filter=${status}`);
      setSubmissions(res.data?.submissions || []);
    } catch (e) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const openDetail = async (sub) => {
    setDetail({ ...sub, entries: [] });
    setDetailLoading(true);
    try {
      const res = await api.get(`/time-tracking/submissions/${sub.id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error('Failed to load submission');
    } finally {
      setDetailLoading(false);
    }
  };

  const allowCorrection = async (s) => {
    if (!window.confirm('Reopen this approved timesheet so the employee can correct and resubmit?')) return;
    setBusy(true);
    try {
      await api.post(`/time-tracking/submissions/${s.id}/correction/approve`, {});
      toast.success('Timesheet reopened for correction');
      load(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve correction');
    } finally {
      setBusy(false);
    }
  };

  const denyCorrection = async (s) => {
    const note = window.prompt('Optional note for the employee (leave blank to skip):', '');
    if (note === null) return;
    setBusy(true);
    try {
      await api.post(`/time-tracking/submissions/${s.id}/correction/deny`, { note });
      toast.success('Correction request denied');
      load(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deny correction');
    } finally {
      setBusy(false);
    }
  };

  const approve = async (sub) => {
    setBusy(true);
    try {
      await api.post(`/time-tracking/submissions/${sub.id}/approve`, {});
      toast.success('Submission approved');
      setDetail(null);
      load(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!rejectNote.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/time-tracking/submissions/${rejecting.id}/reject`, { review_note: rejectNote.trim() });
      toast.success('Submission returned to employee');
      setRejecting(null);
      setRejectNote('');
      setDetail(null);
      load(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    } finally {
      setBusy(false);
    }
  };

  const exportTimesheet = async (sub) => {
    try {
      const res = await api.get(`/time-tracking/export-timesheet/${sub.employee_id}?week_ending=${sub.week_ending}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `timesheet_${(sub.employee_name || 'employee').replace(/\s+/g, '_')}_${sub.week_ending}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export timesheet');
    }
  };

  const statusBadge = (status) => {
    if (status === 'approved') return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Approved</Badge>;
    if (status === 'rejected') return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Returned</Badge>;
    return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Pending</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="timesheet-approvals-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6" />
            Timesheet Approvals
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Review timesheets submitted by employees. Approve to lock for payroll, or return with a note.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={sendFridayReminders} disabled={sendingReminders} data-testid="send-reminders-btn">
            {sendingReminders ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
            Send Friday Reminders
          </Button>
          <Button variant="outline" onClick={sendDigest} disabled={sendingDigest} data-testid="send-digest-btn">
            {sendingDigest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Email Digest to Admins
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="submitted" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Returned</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {loading ? 'Loading...' : `${submissions.length} submissions`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : submissions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No submissions in this state.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="py-2 pr-3">Employee</th>
                        <th className="py-2 pr-3">Week Ending</th>
                        <th className="py-2 pr-3">Entries</th>
                        <th className="py-2 pr-3">Hours</th>
                        <th className="py-2 pr-3">Mileage</th>
                        <th className="py-2 pr-3">Expenses</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Submitted</th>
                        <th className="py-2 pr-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((s, idx) => (
                        <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`submission-row-${idx}`}>
                          <td className="py-2 pr-3 font-medium">{s.employee_name}</td>
                          <td className="py-2 pr-3">{s.week_ending}</td>
                          <td className="py-2 pr-3">{s.entry_count}</td>
                          <td className="py-2 pr-3">{s.total_hours}</td>
                          <td className="py-2 pr-3">{s.total_mileage}</td>
                          <td className="py-2 pr-3">${(s.total_expenses || 0).toFixed(2)}</td>
                          <td className="py-2 pr-3">
                            {statusBadge(s.status)}
                            {s.correction_requested && (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 ml-1" data-testid={`correction-badge-${idx}`}>
                                Correction Requested
                              </Badge>
                            )}
                            {s.correction_requested && s.correction_reason && (
                              <div className="text-xs text-amber-700 mt-1 max-w-[240px] truncate" title={s.correction_reason}>
                                "{s.correction_reason}"
                              </div>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-muted-foreground text-xs">
                            {s.submitted_at ? format(new Date(s.submitted_at), 'MMM d, h:mm a') : '—'}
                          </td>
                          <td className="py-2 pr-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="outline" size="sm" onClick={() => openDetail(s)} data-testid={`view-submission-${idx}`}>
                                View
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => exportTimesheet(s)} data-testid={`export-submission-${idx}`}>
                                <FileSpreadsheet className="w-3 h-3 mr-1" /> XLSX
                              </Button>
                              {s.status === 'submitted' && (
                                <>
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => approve(s)} disabled={busy} data-testid={`approve-submission-${idx}`}>
                                    <Check className="w-3 h-3 mr-1" /> Approve
                                  </Button>
                                  <Button size="sm" variant="destructive" onClick={() => { setRejecting(s); setRejectNote(''); }} disabled={busy} data-testid={`reject-submission-${idx}`}>
                                    <X className="w-3 h-3 mr-1" /> Return
                                  </Button>
                                </>
                              )}
                              {s.status === 'approved' && s.correction_requested && (
                                <>
                                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => allowCorrection(s)} disabled={busy} data-testid={`allow-correction-${idx}`}>
                                    <Check className="w-3 h-3 mr-1" /> Allow Correction
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => denyCorrection(s)} disabled={busy} data-testid={`deny-correction-${idx}`}>
                                    <X className="w-3 h-3 mr-1" /> Deny
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {detail?.employee_name} — Week ending {detail?.week_ending}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : detail && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <div><div className="text-muted-foreground text-xs">Status</div>{statusBadge(detail.status)}</div>
                <div><div className="text-muted-foreground text-xs">Entries</div><div>{detail.entry_count}</div></div>
                <div><div className="text-muted-foreground text-xs">Total Hours</div><div>{detail.total_hours}</div></div>
                <div><div className="text-muted-foreground text-xs">Mileage</div><div>{detail.total_mileage}</div></div>
              </div>
              {detail.review_note && (
                <div className="text-xs bg-muted/40 border rounded p-2"><strong>Review note:</strong> {detail.review_note}</div>
              )}
              <div className="overflow-x-auto max-h-80">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left border-b sticky top-0 bg-background">
                      <th className="py-1 pr-2">Date</th>
                      <th className="py-1 pr-2">Job</th>
                      <th className="py-1 pr-2">Start</th>
                      <th className="py-1 pr-2">End</th>
                      <th className="py-1 pr-2 text-right">Hours</th>
                      <th className="py-1 pr-2 text-right">Miles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.entries || []).map((e) => (
                      <tr key={e.id} className="border-b last:border-0">
                        <td className="py-1 pr-2">{e.date}</td>
                        <td className="py-1 pr-2">{e.job_title}</td>
                        <td className="py-1 pr-2">{e.start_time}</td>
                        <td className="py-1 pr-2">{e.end_time}</td>
                        <td className="py-1 pr-2 text-right">{e.hours}</td>
                        <td className="py-1 pr-2 text-right">{e.miles_driven || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
            {detail?.status === 'submitted' && (
              <>
                <Button variant="destructive" onClick={() => { setRejecting(detail); setRejectNote(''); }} disabled={busy}>
                  <X className="w-4 h-4 mr-1" /> Return
                </Button>
                <Button className="bg-green-600 hover:bg-green-700" onClick={() => approve(detail)} disabled={busy}>
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o) { setRejecting(null); setRejectNote(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return timesheet to {rejecting?.employee_name}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Reason (sent to the employee)</Label>
            <Textarea
              id="reject-reason"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="e.g., Missing mileage on Tuesday; please add and resubmit."
              rows={4}
              data-testid="reject-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejecting(null); setRejectNote(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={reject} disabled={busy} data-testid="confirm-reject-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Return to Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
